import * as Sentry from '@sentry/react';

import {
  isExpectedApiOutcomeEvent,
  spaziousContextIntegration,
} from './spaziousContextIntegration.js';

// A developer's machine must never pollute the shared issue stream, and a local
// build has no uploaded source maps anyway, so its stacks would be unreadable.
// `location.hostname` returns IPv6 hosts bracketed, hence `[::1]`.
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

const isLocalHost = (): boolean =>
  typeof window !== 'undefined' && LOCAL_HOSTNAMES.has(window.location.hostname);

// `yarn dev` is `vite --host`, so the dev server is routinely reached over a LAN
// IP or an `/etc/hosts` alias — neither of which is a loopback hostname. Pointing
// a local session at a deployed `.env` (a normal workflow here) then supplies a
// real DSN and a non-`development` `SPZ_APP_ENV`, so the hostname check alone
// would let a developer's machine report. `import.meta.env.DEV` is the airtight
// signal: verified `false` for `vite build --mode=development`, which is how the
// deployed development environment is built (`Dockerfile`), so this cannot
// silence Sentry on a real deployment.
const isDevServer = (): boolean => import.meta.env.DEV;

/**
 * Initialize Sentry for the Backoffice.
 *
 * Skipped on the Vite dev server and on local hosts — every deployed
 * environment reports, `dev` included. `release` matches the name the Vite
 * plugin uploads source maps under, which is what keeps stacks symbolicated.
 */
export const initSentry = (): void => {
  const dsn = import.meta.env.SPZ_APP_SENTRY_DSN;
  const environment = import.meta.env.SPZ_APP_ENV;

  if (!dsn || isDevServer() || isLocalHost()) {
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release: `${import.meta.env.SPZ_APP_VERSION}-${import.meta.env.SPZ_APP_COMMIT_HASH}`,
    // No email, no IP, no cookies. Approximate country/region, when the Sentry
    // project is configured to derive it, comes from the connecting IP at ingest
    // and never from anything sent here.
    sendDefaultPii: false,
    // Our integration must observe events before `Dedupe` collapses them, so it is
    // prepended to the defaults rather than appended (the array order is the event
    // processing order). Every default integration is preserved.
    integrations: (defaultIntegrations) => [
      spaziousContextIntegration(),
      ...defaultIntegrations,
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: true,
        // Replay samples on the event as it was sent, i.e. after the integration
        // above tagged it, so an expected backend outcome burns no replay.
        beforeErrorSampling: (event) => !isExpectedApiOutcomeEvent(event),
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // set to true to debug in console
    debug: false,
  });
};

export default initSentry;
